import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { resetStorePlugin } from '@/stores/reset-plugin'
import { createHead } from '@unhead/vue/client'
import router from './router'
import App from './App.vue'
// The library stylesheet goes first so main.css (Workbench toast theme, `.wb-toasts`) wins ties.
import "vue-toastification/dist/index.css"
import './assets/main.css'
import { useAuthStore } from '@/stores/auth'
import { setupDeviceTokenBridge } from '@/lib/device-token'
import { initAnalytics } from '@/lib/analytics'
import { installStaleChunkReload } from '@/lib/stale-chunk-reload'
import Toast from "vue-toastification"

const app = createApp(App)
const pinia = createPinia()
pinia.use(resetStorePlugin)
const head = createHead()

app.use(pinia)
app.use(router)
app.use(head)
// vue-toastification (demo read-only guard, org fallback, OrgSwitcher and a few settings views).
// Themed to match components/ui/ToastNotifications.vue: bottom-left of the content area on desktop,
// above the mobile tab bar below 1024px; the look lives in main.css under `.wb-toasts`. The colour
// comes from a CSS dot per type, so the library icon and progress bar are off. Call sites are
// unchanged.
app.use(Toast, {
  position: "bottom-left",
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: true,
  closeButton: "button",
  icon: false,
  rtl: false,
  containerClassName: "wb-toasts",
  accessibility: { toastRole: "status", closeButtonLabel: "Close" },
  toastDefaults: {
    error: { accessibility: { toastRole: "alert", closeButtonLabel: "Close" } },
  },
  onMounted: (component: unknown) => {
    // The library's root is a bare <div>. Below 768px main.css transforms every element, which
    // would pin the fixed stack inside it to that div instead of the viewport (main.css resets
    // `.wb-toasts-root`); aria-live makes it a live region that exists before the first toast.
    const el = (component as { $el?: HTMLElement }).$el
    el?.classList?.add("wb-toasts-root")
    el?.setAttribute?.("aria-live", "polite")
  },
})

// scripts/prerender.mjs injects window.__PRERENDER_INJECTED.isPrerender=true
// before any script runs. In that mode we skip auth-init (no API in the build
// sandbox) and set window.__PRERENDER_READY so the prerender script knows the
// SPA has mounted and the head/route content is final.
const isPrerender = !!(window as { __PRERENDER_INJECTED?: { isPrerender?: boolean } }).__PRERENDER_INJECTED?.isPrerender

if (isPrerender) {
  router.isReady().then(() => {
    app.mount('#app')
    nextTick(() => {
      ;(window as { __PRERENDER_READY?: boolean }).__PRERENDER_READY = true
    })
  })
} else {
  installStaleChunkReload(router)
  initAnalytics()
  setupDeviceTokenBridge()
  const authStore = useAuthStore()
  authStore.initializeAuth().then(() => {
    app.mount('#app')
  })
}